// SP the sankey functions themselves. Have made some tweaks here
import d3 from "d3";

const getValue = function (link) {
  return link.value;
};

const getNodeWithId = function (nodes, id) {
  const ourId = id;
  const found = nodes.filter(function (e) {
    return e["node"] === ourId;
  });

  return found[0];
};

// Populate the outgoingLinks and incomingLinks for each node.
// Also, if the source and target are not objects, assume they are indices.
const computeNodeLinks = function (sankey) {
  const nodes = sankey.nodes;
  const links = sankey.links;
  nodes.forEach(function (node) {
    node.outgoingLinks = [];
    node.incomingLinks = [];
  });

  links.forEach(function (link) {
    let source = link.source;
    let target = link.target;
    if (typeof source === "number") source = link.source = getNodeWithId(nodes, link.source);
    if (typeof target === "number") target = link.target = getNodeWithId(nodes, link.target);
    // when the target is a floating associate it should not have incoming links(and so its source should not have an outgoing link)
    if (!target.floating_associate) {
      source.outgoingLinks.push(link);
      target.incomingLinks.push(link);
    }
  });
};

// Compute the value of each node by summing the associated links. This determines what portion of the column's height
// this node will take up.
const computeNodeValues = function (sankey) {
  const nodes = sankey.nodes;
  const nonTerminalNodePaddingFactor = 0.8;

  nodes.forEach(function (node) {
    if (node.floating_associate) {
      node.value = 0.1;
    } else if (node.outgoingLinks.length == 0) {
      // SP: if the node is a sink, its value (aka its height), is equal to the count of its incoming links.
      // In nearly all cases this should be 1.
      // Also don't do this for associate floating nodes.
      node.value = d3.sum(node.incomingLinks, getValue);
    } else {
      // SI: the nonTerminalNodePaddingFactor determines how much extra vertical space nodes with lots of (usually)
      // outgoing links get. It's a visual thing and should be eyeballed with some example charts when changed.
      // Mostly affects very busy charts like Veolia's or Suez's.
      node.value = nonTerminalNodePaddingFactor * Math.max(
        d3.sum(node.outgoingLinks, getValue),
        d3.sum(node.incomingLinks, getValue));
    }
  });
};

// Iteratively assign the breadth (x-position) for each node.
// Nodes are assigned the maximum breadth of incoming neighbors plus one;
// nodes with no incoming links are assigned breadth zero, while
// nodes with no outgoing links are assigned the maximum breadth.
const computeNodeBreadths = function (sankey, nodeWidth, size) {
  let remainingNodes = sankey.nodes;
  let nextNodes;
  let x = 0;

  while (remainingNodes.length) {
    nextNodes = [];
    remainingNodes.forEach(function (node) {
      node.x = x;
      node.dx = nodeWidth;
      node.outgoingLinks.forEach(function (link) {
        nextNodes.push(link.target);
      });
    });
    remainingNodes = nextNodes;
    ++x;
  }

  const kx = (size.width - nodeWidth) / (x - 1);
  sankey.nodes.forEach(function (node) {
    node.x *= kx;
  });
};

const maxParentY = function (d) {
  return d3.max(d.incomingLinks, (d) => d.source.y);
};

const initializeNodeDepth = function (nodesByBreadth, size) {
  nodesByBreadth.forEach(function (nodes) {
    const totalValue = d3.sum(nodes, (d) => d.value);
    const ascendingSorter = function (a, b) {
      // Return < 0 means a goes before b.
      // Return > 0 means be goes before a.
      // Return === 0 leaves the elements unchanged.
      if (a.incomingLinks.length === 0 || b.incomingLinks.length === 0) { return 0; }
      if (a.floating_associate && b.floating_associate) { return 1; }
      if (a.floating_associate && !b.floating_associate) { return 1; }
      if (!a.floating_associate && b.floating_associate) { return -1; }

      return maxParentY(a) - maxParentY(b);
    };

    // SI: This prevents overlapping lines.
    // It sorts the nodes IN PLACE based on the y position of the incoming links.
    nodes.sort(ascendingSorter);

    nodes.forEach(function (node, i) {
      const priorNode = i > 0 ? nodes[i - 1] : false;
      const slotCount = node.value / totalValue;
      const nodeHeight = size.height * slotCount;

      node.y = priorNode ? (priorNode.yBottom + nodeHeight / 2) : nodeHeight / 2;
      node.yBottom = node.y + nodeHeight / 2;
    });
  });
};

const computeNodeDepths = function (sankey, size) {
  // SI: we need the node column-buckets to be ordered, because we want initializeNodedepth to assign node.y values
  // starting with the leftmost nodes and moving right.
  // This is because we sort child nodes based on the node.y values of their parents.
  // v7 removed d3.nest. d3.groups returns [[key, values], ...]; sort by key asc, map to values.
  const nodesByBreadth = d3.groups(sankey.nodes, function (d) { return d.x; })
                           .sort(function (a, b) { return d3.ascending(a[0], b[0]); })
                           .map(function (d) { return d[1]; });
  initializeNodeDepth(nodesByBreadth, size);
};

const d3Sankey = function (nodes, links, sz) {
  const sankey = { nodes, links };
  const nodeWidth = 1;
  const size = sz;

  computeNodeLinks(sankey);
  computeNodeValues(sankey);
  computeNodeBreadths(sankey, nodeWidth, size);
  computeNodeDepths(sankey, size);

  return sankey;
};

export default d3Sankey;
